'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Send } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string) => void;
  onTyping?: () => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
}

export default function ChatInput({ onSend, onTyping, disabled, placeholder = 'Type a message...', maxLength = 1000 }: ChatInputProps) {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingThrottleRef = useRef<number>(0);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.focus();
    }
  }, [text, disabled, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // Auto-expand up to 4 lines
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
    // Throttle typing indicator to once per 2 seconds
    if (onTyping && Date.now() - typingThrottleRef.current > 2000) {
      typingThrottleRef.current = Date.now();
      onTyping();
    }
  };

  return (
    // Matches the AI Assistant composer (compact: text-xs, rounded-lg, small Send).
    <div className="border-t border-default p-2 flex items-end gap-1.5">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={1}
        disabled={disabled}
        className="flex-1 bg-surface border border-default rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-slate-500 resize-none focus:outline-none focus:border-sky-500 disabled:opacity-50"
        style={{ minHeight: '30px', maxHeight: '96px' }}
      />
      <button
        onClick={handleSend}
        disabled={disabled || !text.trim()}
        className="h-[30px] px-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-40 disabled:hover:bg-sky-600 text-white transition-colors flex items-center justify-center flex-shrink-0"
      >
        <Send size={14} />
      </button>
    </div>
  );
}
